import { useEffect, useRef, useState } from 'react';
import { Bluetooth, Check, ChevronLeft, Lightbulb, RotateCcw } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import CampoContrasena from '../ui/CampoContrasena';
import Motivo from '../experiencia/Motivo';
import { recortarNombre, faltaEnDonde } from './codigoDeTerminal';
import CodigoDeActivacion from './CodigoDeActivacion';
import { leerErrorDelContrato } from './errorDelContrato';
import { CamposDonde, ErrorDelContrato } from './VincularTerminal';
import {
  ESTADO, fuerzaDeSenal, motivoDeContrasena, motivoDeRed, soloNumeros, textoDeError, textoDeEstado,
} from './cfgBle';
import { ConexionCfg, ErrorDeConexion, elegirTerminal } from './conexionBle';

/* =====================================================================
   «Buscar terminal cercana» · configurar la terminal por Bluetooth
   =====================================================================
   Contrato v2 2.5.0 §16. Pedido del dueño (10 oct 2026): configurar la
   terminal desde la laptop en menos de 2 minutos SIN desconectar la
   laptop de su red. Una sola pantalla guiada con su progreso:

     1 · elegir TEGO-XXXX en la ventana del navegador
     2 · escribir los 4 números que muestra la terminal (nunca viajan)
     3 · elegir el Wi-Fi de la lista que manda la terminal + contraseña
     4 · nombre y repisa → `credencial-por-serie` → CREDENCIAL y WIFI
     5 · la terminal se apaga, prueba la red y vuelve → «¡Lista!»

   LA CONTRASEÑA DEL WI-FI: solo en el estado de esta pantalla mientras se
   escribe; viaja cifrada a la terminal en el mensaje WIFI, se borra del
   estado AL ENVIARLA, y nunca va al servidor ni se guarda.

   Solo se monta si `hayBluetooth()` dijo que sí (AgregarTerminal); si no,
   el asistente de siempre, sin mensajes técnicos.
   ===================================================================== */

const PASOS = [
  { n: 1, nombre: 'Elegir' },
  { n: 2, nombre: 'Números' },
  { n: 3, nombre: 'Wi-Fi' },
  { n: 4, nombre: 'Nombre' },
  { n: 5, nombre: 'Lista' },
];
const PASO_DE_FASE = { inicio: 1, buscando: 1, numeros: 2, redes: 3, donde: 4, enviando: 5, esperando: 5, lista: 5 };

const BOTON = 'min-h-11 w-full rounded-xl px-3 text-sm font-semibold disabled:opacity-50';
const PRIMARIO = `${BOTON} bg-[var(--color-marca)] text-white`;
const SECUNDARIO = `${BOTON} border border-gray-300 bg-white text-gray-800`;
const ENLACE = 'min-h-11 inline-flex items-center gap-1 text-sm font-semibold text-gray-700';
const ERRORES = { 403: 'SIN_PERMISO', 409: 'YA_VINCULADA', 429: 'DEMASIADAS_PETICIONES' };

function Progreso({ paso }) {
  return (
    <nav aria-label="Progreso" className="rounded-xl border border-gray-200 bg-white p-3" data-progreso-ble="">
      <p className="text-xs font-medium text-gray-600">Paso {paso} de {PASOS.length}</p>
      <ol className="mt-2 grid grid-cols-5 gap-x-1 text-xs">
        {PASOS.map((p) => (
          <li key={p.n} className={`flex items-start gap-1 break-words ${p.n === paso ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
            {p.n < paso && <Check className="mt-0.5 h-3 w-3 shrink-0 text-green-700" aria-hidden="true" />}
            <span className="min-w-0">{p.nombre}</span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Aviso({ texto }) {
  if (!texto) return null;
  return (
    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-error-ble="">
      <strong className="block break-words">{texto}</strong>
    </div>
  );
}

/** Un texto que no mande a hacer algo inútil: si el navegador no puede
 *  cifrar, acercar la terminal no cambia nada. */
function mensajeDeFallo(err) {
  if (err instanceof ErrorDeConexion) return err.message;
  if (err?.name === 'NotSupportedError') {
    return 'Este navegador no puede configurar la terminal por Bluetooth. Usa el código de la pantalla o actualiza Chrome o Edge.';
  }
  return 'No se pudo conectar con la terminal. Revisa que su pantalla diga TEGO- y vuelve a empezar, o usa el código de la pantalla.';
}

export default function ConfigurarPorBluetooth({ onCerrar, onLista, onOtraForma, onVerTerminales }) {
  const [fase, setFase] = useState('inicio');
  const [estadoTexto, setEstadoTexto] = useState('');
  const [error, setError] = useState(null);
  const [errorServidor, setErrorServidor] = useState(null);
  const [numeros, setNumeros] = useState('');
  const [redes, setRedes] = useState([]);
  const [red, setRed] = useState('');
  const [clave, setClave] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [lugares, setLugares] = useState({ sucursales: [], ubicaciones: [] });
  const [donde, setDonde] = useState({ id_sucursal: '', id_ubicacion: '', nombre: '' });
  const [trabajando, setTrabajando] = useState(false);
  const [lista, setLista] = useState(null);
  const conexion = useRef(null);
  const entregada = useRef(false);
  const info = useRef(null);

  useEffect(() => {
    let vivo = true;
    Promise.all([api.get('/sucursales').catch(() => []), api.get('/ubicaciones').catch(() => [])])
      .then(([s, u]) => {
        if (!vivo) return;
        const sucursales = Array.isArray(s) ? s : (s?.sucursales || []);
        const ubicaciones = (Array.isArray(u) ? u : (u?.ubicaciones || [])).filter((x) => x.activo !== false);
        setLugares({ sucursales, ubicaciones });
        setDonde((d) => ({
          ...d,
          id_sucursal: d.id_sucursal || (sucursales.length === 1 ? String(sucursales[0].id) : ''),
          id_ubicacion: d.id_ubicacion || (ubicaciones.length === 1 ? String(ubicaciones[0].id) : ''),
        }));
      });
    return () => { vivo = false; conexion.current?.desconectar(); };
  }, []);

  function volverAEmpezar() {
    conexion.current?.desconectar();
    conexion.current = null;
    entregada.current = false;
    info.current = null;
    setFase('inicio'); setError(null); setErrorServidor(null); setNumeros('');
    setRedes([]); setRed(''); setClave(''); setVerClave(false); setLista(null); setEstadoTexto('');
  }

  async function buscar() {
    setError(null);
    let disp;
    try {
      disp = await elegirTerminal();
    } catch {
      setError('No se eligió ninguna terminal. Si la tuya no sale en la lista, sostén el botón de la terminal 5 segundos y vuelve a buscar.');
      return;
    }
    setFase('buscando');
    setEstadoTexto('Conectando con la terminal…');
    try {
      const c = new ConexionCfg(disp);
      conexion.current = c;
      c.onEstado((e) => {
        if (e.estado !== ESTADO.ERROR) setEstadoTexto(textoDeEstado(e));
      });
      await c.conectar();
      info.current = await c.leerInfo();
      setDonde((d) => ({ ...d, nombre: d.nombre || recortarNombre(`Terminal ${String(info.current.serie).slice(-4)}`) }));
      const pide = c.esperarEstado((e) => e.estado === ESTADO.ESPERANDO_CODIGO);
      await c.saludar();
      const e = await pide;
      if (e.estado === ESTADO.ERROR) throw new ErrorDeConexion(textoDeError(e));
      setFase('numeros');
    } catch (err) {
      // Lo que falle a mitad del pareo cierra la conexión y atiende lo que
      // estaba esperando (auditoría BLE P1-1).
      conexion.current?.desconectar();
      conexion.current = null;
      setError(mensajeDeFallo(err));
      setFase('inicio');
    }
  }

  async function confirmar(ev) {
    ev.preventDefault();
    if (numeros.length !== 4) return;
    setTrabajando(true); setError(null);
    try {
      const e = await conexion.current.confirmar(numeros);
      if (e.estado === ESTADO.ERROR) { setError(textoDeError(e)); setNumeros(''); return; }
      const lista = await conexion.current.pedirRedes();
      setRedes(lista);
      setRed(lista.length === 1 ? lista[0].nombre : '');
      setFase('redes');
    } catch (err) {
      setError(err instanceof ErrorDeConexion ? err.message : 'Algo se perdió en el camino. Intenta otra vez.');
    } finally {
      setTrabajando(false);
    }
  }

  const redElegida = redes.find((r) => r.nombre === red);
  const pideClave = !redElegida || redElegida.conClave;
  const motivoRed = motivoDeRed(red);
  const motivoClave = pideClave ? (clave === '' ? 'Escribe la contraseña de tu Wi-Fi.' : motivoDeContrasena(clave)) : null;
  const faltaDonde = faltaEnDonde(donde, lugares.ubicaciones);

  async function conectarYVincular(ev) {
    ev.preventDefault();
    if (faltaDonde || motivoRed || motivoClave) return;
    const c = conexion.current;
    setTrabajando(true); setError(null); setErrorServidor(null);
    try {
      if (!entregada.current) {
        let d;
        try {
          d = await api.post('/terminal/v2/credencial-por-serie', {
            serie: info.current.serie,
            id_sucursal: donde.id_sucursal ? Number(donde.id_sucursal) : null,
            id_ubicacion: Number(donde.id_ubicacion),
            nombre: recortarNombre(donde.nombre).trim(),
            firmware: info.current.firmware || undefined,
            contrato: info.current.contrato || undefined,
          });
        } catch (err) {
          setErrorServidor(leerErrorDelContrato(err, { porEstado: ERRORES, terminal: 'esta terminal' }));
          return;
        }
        await c.enviarCredencial(d);
        entregada.current = d;
      }
      const laClave = pideClave ? clave : '';
      setClave(''); setVerClave(false);           // se borra AL ENVIARLA
      setFase('esperando');
      setEstadoTexto(`La terminal se está conectando a «${red}»…`);
      await c.enviarWifi(red, laClave);
      const final = await c.vueltaPorElWifi();
      if (final.estado === ESTADO.VINCULADA) {
        await c.terminar();
        const r = { estacion: final.texto || entregada.current?.estacion?.nombre_corto || donde.nombre };
        setLista(r); setFase('lista');
        onLista?.(r);
        return;
      }
      if (final.estado === ESTADO.ERROR && [0x05, 0x06, 0x07, 0x0b].includes(final.detalle)) {
        setError(textoDeError(final)); setFase('redes');           // misma sesión: otra vez la red
        return;
      }
      setError(final.estado === ESTADO.ERROR ? textoDeError(final) : textoDeEstado(final));
      setFase('error');
    } catch (err) {
      setError(err instanceof ErrorDeConexion ? err.message : 'Algo se perdió en el camino. Intenta otra vez.');
      setFase('error');
    } finally {
      setTrabajando(false);
    }
  }

  async function identificar() {
    try { await conexion.current?.identificar(); } catch { /* si no está, el botón no hace daño */ }
  }

  const paso = PASO_DE_FASE[fase] || 1;
  const conectada = ['numeros', 'redes', 'donde'].includes(fase);

  return (
    <div className="space-y-3" data-terminal="bluetooth">
      {onCerrar && (
        <button type="button" onClick={() => { volverAEmpezar(); onCerrar(); }} className={ENLACE}>
          <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Volver
        </button>
      )}
      <h2 className="text-base font-semibold text-gray-900">Buscar terminal cercana</h2>
      <p className="text-sm text-gray-600">Configura la terminal desde esta computadora por Bluetooth, sin cambiar de red.</p>
      <Ayuda id="terminal.bluetooth.pantalla" />
      {fase !== 'error' && <Progreso paso={paso} />}

      {estadoTexto && fase !== 'inicio' && fase !== 'lista' && fase !== 'error' && (
        <p role="status" aria-live="polite" className="text-sm text-gray-700" data-estado-ble="">
          {estadoTexto}
        </p>
      )}
      <Aviso texto={error} />
      <ErrorDelContrato error={errorServidor} id="error-ble-servidor" />
      {/* F1c: una terminal de fábrica sin dueño pide el código de su caja
          antes de recibir su credencial; activada, se vuelve a pulsar. */}
      {errorServidor?.codigo === 'SIN_ACTIVAR' && info.current?.serie && (
        <CodigoDeActivacion serie={info.current.serie} textoBoton="Activar y seguir"
                            onActivada={() => setErrorServidor(null)} />
      )}

      {fase === 'inicio' && (
        <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-fase="inicio">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-700">
            <li>Conecta la terminal a la corriente, cerca de esta computadora.</li>
            <li>Pulsa «Buscar» y elige la terminal <strong>TEGO-</strong> que dice su pantalla.</li>
          </ol>
          <button type="button" onClick={buscar} className={PRIMARIO} data-accion="buscar-ble">
            <span className="inline-flex items-center justify-center gap-2"><Bluetooth className="h-4 w-4" aria-hidden="true" /> Buscar</span>
          </button>
          {onOtraForma && (
            <button type="button" onClick={onOtraForma} className={SECUNDARIO}>Usar el código de la pantalla</button>
          )}
        </section>
      )}

      {fase === 'buscando' && (
        <p className="text-sm text-gray-600" aria-busy="true">Un momento…</p>
      )}

      {fase === 'numeros' && (
        <form onSubmit={confirmar} className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-fase="numeros">
          <label htmlFor="ble-numeros" className="block text-sm font-medium text-gray-700">Escribe los 4 números que muestra la terminal</label>
          <input id="ble-numeros" className="entrada text-center font-mono text-2xl tracking-widest" inputMode="numeric"
                 autoComplete="one-time-code" value={numeros} maxLength={4} placeholder="0000" disabled={trabajando}
                 onChange={(e) => setNumeros(soloNumeros(e.target.value))} />
          <Ayuda id="terminal.bluetooth.numeros" />
          <Motivo id="motivo-ble-numeros" cuando={numeros.length !== 4 && !trabajando}>
            {`Faltan ${4 - numeros.length} ${4 - numeros.length === 1 ? 'número' : 'números'}.`}
          </Motivo>
          <button type="submit" className={PRIMARIO} disabled={numeros.length !== 4 || trabajando}
                  aria-describedby={numeros.length !== 4 ? 'motivo-ble-numeros' : undefined}>
            {trabajando ? 'Comprobando…' : 'Confirmar'}
          </button>
        </form>
      )}

      {fase === 'redes' && (
        <form onSubmit={(e) => { e.preventDefault(); if (!motivoRed && !motivoClave) setFase('donde'); }}
              className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-fase="redes">
          <fieldset>
            <legend className="text-sm font-medium text-gray-700">Elige tu red Wi-Fi</legend>
            {redes.length === 0 && (
              <p className="mt-1 text-sm text-gray-600">La terminal no vio ninguna red al encender. Acércala al módem y vuelve a empezar.</p>
            )}
            <div className="mt-2 space-y-2">
              {redes.map((r) => (
                <label key={r.nombre} className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border px-3 ${red === r.nombre ? 'border-[var(--color-marca)] bg-gray-50' : 'border-gray-200'}`}>
                  <input type="radio" name="ble-red" value={r.nombre} checked={red === r.nombre} onChange={() => setRed(r.nombre)} />
                  <span className="min-w-0 flex-1 break-words text-sm text-gray-900">{r.nombre}</span>
                  <span className="shrink-0 text-xs text-gray-600">{fuerzaDeSenal(r.rssi)}{r.conClave ? ' · con contraseña' : ' · abierta'}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {pideClave && (
            <div>
              <label htmlFor="ble-clave" className="block text-sm font-medium text-gray-700">Contraseña del Wi-Fi</label>
              <CampoContrasena id="ble-clave" className="entrada mt-1" value={clave} autoComplete="off"
                               visible={verClave} onVisible={setVerClave} onChange={(e) => setClave(e.target.value)} />
              <Ayuda id="terminal.bluetooth.contrasena" />
            </div>
          )}
          <Motivo id="motivo-ble-red" cuando={!!(motivoRed || motivoClave)}>{motivoRed || motivoClave}</Motivo>
          <button type="submit" className={PRIMARIO} disabled={!!(motivoRed || motivoClave)}
                  aria-describedby={motivoRed || motivoClave ? 'motivo-ble-red' : undefined}>Siguiente</button>
        </form>
      )}

      {fase === 'donde' && (
        <form onSubmit={conectarYVincular} className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-fase="donde">
          <p className="text-sm font-medium text-gray-700">Ponle nombre y dile dónde trabaja</p>
          <CamposDonde idBase="ble" sucursales={lugares.sucursales} ubicaciones={lugares.ubicaciones}
                       valor={donde} onCambio={setDonde} />
          <Motivo id="motivo-ble-donde" cuando={!!faltaDonde && !trabajando}>{faltaDonde}</Motivo>
          <button type="submit" className={PRIMARIO} disabled={!!faltaDonde || trabajando}
                  aria-describedby={faltaDonde ? 'motivo-ble-donde' : undefined}>
            {trabajando ? 'Enviando…' : 'Conectar y vincular'}
          </button>
          <button type="button" onClick={() => setFase('redes')} className={SECUNDARIO}>Cambiar la red</button>
        </form>
      )}

      {fase === 'esperando' && (
        <section className="space-y-2 rounded-xl border border-gray-200 bg-white p-4" aria-busy="true" data-fase="esperando">
          <p className="text-sm text-gray-700">La terminal apaga el Bluetooth unos segundos para probar el Wi-Fi y vuelve sola. No cierres esta ventana.</p>
        </section>
      )}

      {fase === 'lista' && lista && (
        <section role="status" className="space-y-3 rounded-xl border border-green-200 bg-green-50 p-4" data-fase="lista">
          <p className="text-base font-semibold text-green-900">{`¡Lista! Vinculada a «${lista.estacion}»`}</p>
          <p className="text-sm text-green-900">Ya trabaja en tu empresa. Puedes desconectarla y llevarla a su lugar.</p>
          {(onVerTerminales || onCerrar) && (
            <button type="button" onClick={onVerTerminales || onCerrar} className={PRIMARIO}>Ver mis terminales</button>
          )}
        </section>
      )}

      {conectada && (
        <div className="space-y-1">
          <button type="button" onClick={identificar} className={SECUNDARIO} data-accion="identificar-ble">
            <span className="inline-flex items-center justify-center gap-2"><Lightbulb className="h-4 w-4" aria-hidden="true" /> Identificar: que parpadee la terminal</span>
          </button>
          <Ayuda id="terminal.bluetooth.identificar" />
        </div>
      )}
      {fase !== 'inicio' && fase !== 'lista' && (
        <button type="button" onClick={volverAEmpezar} className={SECUNDARIO} disabled={fase === 'esperando'}
                aria-describedby={fase === 'esperando' ? 'motivo-ble-esperando' : undefined} data-accion="empezar-ble">
          <span className="inline-flex items-center justify-center gap-2"><RotateCcw className="h-4 w-4" aria-hidden="true" /> Volver a empezar</span>
        </button>
      )}
      <Motivo id="motivo-ble-esperando" cuando={fase === 'esperando'}>Espera a que la terminal vuelva: tarda menos de un minuto.</Motivo>
    </div>
  );
}
